import type { Directive } from 'vue';
import { CloseIconFull } from '@/assets/icons';

export interface ITooltip {
  width?: number | string;
  position?: string;
  value: string | string[];
  visible?: boolean;
  type?: string;
  closable?: boolean;
  title?: string;
  isClickable?: boolean;
}

export interface Binding {
  value: ITooltip;
  dir: any;
  instance: any;
  arg: string;
  oldValue: any;
}
export interface HTMLTooltipTargetElement extends HTMLElement {
  binding?: Binding;
}
export interface ISetPositionAccordingToOverFlow {
  leftPosition: number
  tooltip: HTMLTooltipTargetElement
  tooltipWidth: number
  tooltipHeight: number
  stylePosition: string
  position?: string
  rect: DOMRect
  binding: Binding
}

const SHIFT_AMOUNT = 8;

export default {
  mounted(el: HTMLTooltipTargetElement, binding: Binding) {
    const visible = (binding.value || {}).visible;
    const randomId = (Math.random() + 1).toString(36).substring(7);
    el.setAttribute('data-id', randomId);
    el.binding = binding as Binding;
    if (visible) {
      setTimeout(() => {
        addTooltip(generateEventWithTarget(el), binding);
      }, 100);
    } else {
      removeListeners(el);
      attachListeners(el);
    }
  },
  updated(el: HTMLTooltipTargetElement, binding: Binding) {
    const isHovered = el.getAttribute('data-hover') === 'true';
    const visible = (binding.value || {}).visible;
    el.binding = binding as Binding;

    const mouseEvent = generateEventWithTarget(el);

    if (!visible && !isHovered) {
      attachListeners(el);
    }
    const isOwnProperty = Object.hasOwnProperty.call(binding.value, 'visible');
    if (isOwnProperty && !visible) {
      removeTooltip(mouseEvent);
    } else if (isOwnProperty && visible && !isHovered) {
      addTooltip(mouseEvent, binding);
      updateTooltip(el);
    }
    if (isHovered && !isOwnProperty) {
      updateTooltip(el);
    }
  },
  unmounted(el: HTMLTooltipTargetElement) {
    removeTooltip(generateEventWithTarget(el));
    el.removeAttribute('data-hover');
    removeListeners(el);
    delete el.binding;
  },
  deep: true,
} as Directive;

function setTooltipTemplate(templateProps: ITooltip):string {
  const { value, closable, title } = templateProps;
  let content;
  if (Array.isArray(value)) {
    const valueList = value.map(item => `<li class="mb-1">${item}</li>`).join('');
    content = `<ul class="list-inside list-disc">${valueList}</ul>`;
  } else {
    content = `<div>${value}</div>`;
  }
  return `
  ${title || closable ? `
  <div class="flex justify-between items-center my-1">
    ${title ? `<div>${title}</div>` : ''}
    ${closable ? `<img alt="close-icon" src='${CloseIconFull}' class="ml-auto cursor-pointer" id='tooltipCloseIcon'"/>` : ''}
  </div>
  ` : ''}
  ${content}
`;
}

function addTooltip(event: Event, directiveBinding?: Binding) {
  const el = event.target as HTMLTooltipTargetElement;
  const binding = el.binding as Binding;
  const { width, position, value, visible, type } = binding.value;
  if (!value) {
    return;
  }
  const scopeId = el.getAttribute('data-id');
  const id = `smart-tooltip-${scopeId || 0}`;

  const rect = el.getBoundingClientRect();
  const tooltip = document.createElement('div');
  tooltip.innerHTML = setTooltipTemplate(binding.value);
  if (type) {
    tooltip.classList.add(type);
  }
  if (!document.getElementById(id)) {
    document.body.appendChild(tooltip);
  }

  setTimeout(() => {
    const closeButton = document.querySelector(`#${id} #tooltipCloseIcon`);
    if (closeButton) {
      (el as any).closeButtonListener = () => {
        if (directiveBinding) {
          directiveBinding.value.visible = false;
        }
        if (el.binding) {
          el.binding.value.visible = false;
        }
        removeTooltip(generateEventWithTarget(el));
      };
      closeButton.addEventListener('click', (el as any).closeButtonListener);
    }
  }, 0);
  if (!visible) {
    el.setAttribute('data-hover', 'true');
  }
  if (width) {
    tooltip.style.width = width + 'px';
  }
  tooltip.setAttribute('id', id);
  const { tooltipWidth, tooltipHeight } = calculateTooltipWidthHeight(tooltip);

  const stylePosition = window.innerWidth < 700 ? 'top' : (binding || {}).arg || position || 'bottom';
  const leftPosition = handlePosition({ stylePosition, tooltip, tooltipWidth, tooltipHeight, rect });

  tooltip.classList.add(stylePosition);
  setPositionAccordingToOverflow({ leftPosition, tooltip, tooltipWidth, tooltipHeight, stylePosition, rect, binding, position });

  const newTooltipWidthHeight = calculateTooltipWidthHeight(tooltip);
  if (tooltipWidth !== newTooltipWidthHeight.tooltipWidth || tooltipHeight !== newTooltipWidthHeight.tooltipHeight) {
    const newLeftPosition = handlePosition({
      stylePosition,
      tooltip,
      tooltipWidth: newTooltipWidthHeight.tooltipWidth,
      tooltipHeight: newTooltipWidthHeight.tooltipHeight,
      rect
    });

    setPositionAccordingToOverflow({
      leftPosition: newLeftPosition, tooltip, tooltipWidth: newTooltipWidthHeight.tooltipWidth,
      tooltipHeight: newTooltipWidthHeight.tooltipHeight, stylePosition, rect, binding, position,
    });
  }
}
function setPositionAccordingToOverflow({ leftPosition, tooltip, tooltipWidth, tooltipHeight, stylePosition, rect, position, binding }: ISetPositionAccordingToOverFlow) {
  const lastPosition = handleWindowOverflow({ leftPosition, tooltip, tooltipWidth, stylePosition, tooltipHeight, rect });

  if (position !== lastPosition.leftPosition || lastPosition.adjustmentValue) {
    tooltip.innerHTML = setTooltipTemplate(binding.value);
  }
}
function updateTooltip(el: HTMLTooltipTargetElement) {
  const binding = (el?.binding || {}) as Binding;
  const values = (binding?.value || {}) as ITooltip;
  const { width, value } = values;
  const scopeId = el.getAttribute('data-id');

  const id = `smart-tooltip-${scopeId || 0}`;
  const tooltip = document.getElementById(id);
  if (tooltip) {
    if (width) {
      tooltip.style.width = width + 'px';
    }
    if (value) {
      tooltip.innerHTML = setTooltipTemplate({ ...binding.value, value });
    }
  }
}
function calculateTooltipWidthHeight(tooltip: HTMLElement) {
  const computedStyle = window.getComputedStyle(tooltip);
  return {
    tooltipWidth: parseInt(computedStyle.width, 10) + 1,
    tooltipHeight: parseInt(computedStyle.height, 10) + 1,
  };
}
function handlePosition({
  stylePosition,
  tooltip,
  tooltipWidth,
  tooltipHeight,
  rect,
}: {
  stylePosition: string;
  tooltip: HTMLElement;
  tooltipWidth: number;
  tooltipHeight: number;
  rect: DOMRect;
}) {
  const centerX = rect.left + rect?.width / 2;
  const centerY = rect.top + (rect.bottom - rect.top) / 2;
  let leftPosition = 0;
  switch (stylePosition) {
    case 'bottom':
    case 'top':
      leftPosition = centerX - tooltipWidth / 2;
      break;
    case 'left':
      leftPosition = rect.left - tooltipWidth / 2 - SHIFT_AMOUNT;
      tooltip.style.top = centerY - tooltipHeight / 2 + 'px';
      break;
    case 'right':
      leftPosition = rect.right + SHIFT_AMOUNT;
      tooltip.style.top = centerY - tooltipHeight / 2 + 'px';
      break;
    case 'bottom-left':
    case 'top-left':
      leftPosition = centerX - tooltipWidth + SHIFT_AMOUNT * 2;
      break;
    case 'bottom-right':
    case 'top-right':
      leftPosition = centerX - SHIFT_AMOUNT * 3;
      break;
    case 'bottom-float-left':
    case 'top-float-left':
      leftPosition = rect.left - rect?.width / 2;
      break;
  }
  if (stylePosition.startsWith('bottom')) {
    tooltip.style.top = rect.bottom + SHIFT_AMOUNT + 'px';
  } else if (stylePosition.startsWith('top')) {
    tooltip.style.bottom = window.innerHeight - rect.top + SHIFT_AMOUNT + 'px';
  }
  return leftPosition;
}
function removeListeners(el: HTMLTooltipTargetElement) {
  ['mousemove', 'mouseleave', 'click', 'mouseenter'].forEach((event: string) => {
    const eventName = event as keyof HTMLElementEventMap;
    el.removeEventListener(eventName, removeTooltip);
    el.removeEventListener(eventName, addTooltip);
    el.removeEventListener(eventName, clickHandler);
  });
}
function handleWindowOverflow({
  leftPosition,
  tooltip,
  tooltipWidth,
  stylePosition,
  tooltipHeight,
  rect,
}: {
  leftPosition: number;
  tooltip: HTMLElement;
  tooltipWidth: number;
  stylePosition: string;
  tooltipHeight: number;
  rect: DOMRect;
}) {

  const isOverflowTop = 'top' === stylePosition && (tooltipHeight > rect?.top + SHIFT_AMOUNT);
  const isOverflowBottom = 'bottom' === stylePosition && (tooltipHeight + rect?.bottom + SHIFT_AMOUNT > window.innerHeight);
  let isOverflowRight = false;
  if (stylePosition === 'bottom' || stylePosition === 'top') {
    const difference = (tooltipWidth - rect.width) / 2;
    isOverflowRight = rect?.right + difference >= window.innerWidth;
  } else if (stylePosition === 'right') {
    tooltipWidth = rect.left;
    isOverflowRight = rect?.right + tooltipWidth >= window.innerWidth;
  }
  let adjustmentValue = 0;
  let adjustedPosition = stylePosition;

  if (isOverflowTop) {
    tooltip.style.top = `${rect?.top + rect?.height + SHIFT_AMOUNT}px`;
    tooltip.style.height = `${tooltipHeight}px`;
    adjustedPosition = 'bottom';
  }
  if (isOverflowBottom) {
    tooltip.style.removeProperty('top');
    tooltip.style.bottom = `${window.innerHeight - rect?.bottom + rect?.height + SHIFT_AMOUNT}px`;
    tooltip.style.height = `${tooltipHeight}px`;
    adjustedPosition = 'top';
  }
  if (leftPosition < 0) {
    if (stylePosition === 'left') {
      const centerY = rect.top + (rect.bottom - rect.top) / 2;
      adjustedPosition = 'right';
      tooltip.style.left = `${rect.right + SHIFT_AMOUNT}px`;
      const tooltipWidthHeight = calculateTooltipWidthHeight(tooltip);
      tooltip.style.top = `${centerY - tooltipWidthHeight.tooltipHeight / 2}px`;
    } else {
      tooltip.style.left = '0';
      adjustmentValue = leftPosition;
      tooltip.style.maxWidth = `${tooltipWidth}px`;
      tooltip.style.width = `${tooltipWidth}px`;
    }
  }
  else if (isOverflowRight) {
    const midPoint = rect?.width / 2;

    if (stylePosition === 'right') {
      adjustedPosition = 'left';
      const currentWidth = calculateTooltipWidthHeight(tooltip).tooltipWidth;
      tooltip.style.left = `${rect.left - currentWidth - SHIFT_AMOUNT}px`;
      tooltip.style.maxWidth = `${rect.left - SHIFT_AMOUNT}px`;
      tooltip.style.width = `${currentWidth}px`;
    } else {
      adjustmentValue = (rect?.left + midPoint + (tooltipWidth / 2) - window.innerWidth);
      tooltip.style.left = `${leftPosition - adjustmentValue}px`;
    }
  }
  else {
    if (stylePosition === 'left') {
      const centerY = rect.top + (rect.bottom - rect.top) / 2;
      adjustedPosition = 'left';
      tooltip.style.left = `${rect.left - tooltipWidth - SHIFT_AMOUNT}px`;
      tooltip.style.maxWidth = `${rect.left - SHIFT_AMOUNT}px`;
      tooltip.style.width = `${tooltipWidth}px`;
      const currentHeight = calculateTooltipWidthHeight(tooltip).tooltipHeight;
      tooltip.style.top = `${centerY - currentHeight / 2}px`;
    } else {
      tooltip.style.left = `${leftPosition}px`;
    }
  }

  return { leftPosition: adjustedPosition, adjustmentValue };
}
function removeTooltip(event: Event) {
  const target = event.target as HTMLTooltipTargetElement;
  const binding = target.binding as Binding;
  const values = binding.value;
  if (!values.visible) {
    const scopeId = target.getAttribute('data-id');
    const id = `smart-tooltip-${scopeId || 0}`;
    const tooltipElement = document.getElementById(id);
    if (tooltipElement) {
      document.body.removeChild(tooltipElement);
    }
  }
}
function generateEventWithTarget(target: HTMLTooltipTargetElement, eventType = 'click'):Event {
  const mouseEvent = new Event(eventType);
  Object.defineProperty(mouseEvent, 'target', { value: target, enumerable: true });
  return mouseEvent;
}
function clickHandler(event: Event) {
  const el = event.target as HTMLTooltipTargetElement;
  if (!el.getAttribute('data-hover')) {
    addTooltip(event);
  } else {
    removeTooltip(event);
  }
}
function attachListeners(el: HTMLTooltipTargetElement) {
  const binding = el.binding as Binding;
  const isClickable = (binding.value || {}).isClickable;
  if ((!Object.hasOwnProperty.call(binding.value, 'visible') || !binding.value.visible) && !isClickable) {
    el.addEventListener(
      'mouseenter',
      addTooltip,
      false,
    );
    el.addEventListener(
      'mouseleave',
      removeTooltip,
      false,
    );
  }
  else if (isClickable) {
    el.addEventListener(
      'click',
      clickHandler,
      false,
    );
  }
}
